/**
 * API client for the Creators dashboard — creator and agency report
 * dashboards (persistent, passcode-protected payout pages). Every route needs
 * clientReports.manage ("Creators Dashboard"), enforced server-side.
 */

import { apiRequest, API_BASE_URL, getAuthToken } from './api';
import {
  ChannelGroup,
  ChannelGroupSearch,
  CreatorPageTheme,
  CreatorPageType,
  CreatorReportPage,
} from '../components/Creators/types';

const CREATOR_PAGES = '/api/admin/creator-pages';

/**
 * Every dashboard, each with `channelLabels` for search. `labelsPartial`
 * means the server's earnings index was slow and only stored channel names
 * came back — refetch shortly for the full set.
 */
export async function listCreatorPages(): Promise<{ pages: CreatorReportPage[]; labelsPartial: boolean }> {
  const body = await apiRequest(CREATOR_PAGES);
  return { pages: body.pages ?? [], labelsPartial: body.labelsPartial === true };
}

export interface CreateCreatorPageInput {
  channelIds: string[];
  clientLabel: string;
  /** Absent = 'creator'. 'agency' unlocks the roster layout + 30-channel cap. */
  pageType?: CreatorPageType;
  slug?: string;
  pin?: string;
  includeAdvertisers?: boolean;
  includeAdSense?: boolean;
  /** Absent = allowed (the server default), so only an opt-out is sent. */
  allowDownloads?: boolean;
  avatarUrl?: string;
  bannerUrl?: string;
  theme?: CreatorPageTheme;
}

export async function createCreatorPage(
  input: CreateCreatorPageInput,
): Promise<{ page: CreatorReportPage; url: string }> {
  return apiRequest(CREATOR_PAGES, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface UpdateCreatorPageInput {
  clientLabel?: string;
  includeAdvertisers?: boolean;
  includeAdSense?: boolean;
  allowDownloads?: boolean;
  theme?: CreatorPageTheme;
  avatarUrl?: string;
  /** Replaces the channel roster; capped server-side by the stored pageType. */
  channelIds?: string[];
}

export async function updateCreatorPage(
  slug: string,
  patch: UpdateCreatorPageInput,
): Promise<CreatorReportPage> {
  const body = await apiRequest(`${CREATOR_PAGES}/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return body.page;
}

/** Omit `pin` for a random new one. */
export async function resetCreatorPagePin(slug: string, pin?: string): Promise<CreatorReportPage> {
  const body = await apiRequest(`${CREATOR_PAGES}/${encodeURIComponent(slug)}/reset-pin`, {
    method: 'POST',
    body: JSON.stringify(pin ? { pin } : {}),
  });
  return body.page;
}

export async function revokeCreatorPage(slug: string): Promise<CreatorReportPage> {
  const body = await apiRequest(`${CREATOR_PAGES}/${encodeURIComponent(slug)}/revoke`, {
    method: 'POST',
  });
  return body.page;
}

export async function restoreCreatorPage(slug: string): Promise<CreatorReportPage> {
  const body = await apiRequest(`${CREATOR_PAGES}/${encodeURIComponent(slug)}/restore`, {
    method: 'POST',
  });
  return body.page;
}

export async function deleteCreatorPage(slug: string): Promise<void> {
  await apiRequest(`${CREATOR_PAGES}/${encodeURIComponent(slug)}`, { method: 'DELETE' });
}

/**
 * Host a custom page image and get back its public https URL (usable as
 * avatarUrl on create or PATCH). Raw fetch so the browser sets the multipart
 * boundary — same pattern as importChannelCrmCsv.
 */
export async function uploadCreatorPageImage(file: File): Promise<string> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}${CREATOR_PAGES}/upload-image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }, // no Content-Type — browser sets the boundary
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Image upload failed (${res.status})`);
  return body.url as string;
}

/**
 * Channel picker search: payout groups plus known clients with no payout yet.
 * `lookup` (Enter in the picker) also resolves the query live — a channel URL
 * or @handle through YouTube, a name or email through ChannelMeter.
 */
export async function searchChannelGroups(
  query: string,
  opts: { lookup?: boolean } = {},
): Promise<ChannelGroupSearch> {
  const params = new URLSearchParams({ include: 'new' });
  if (query) params.set('query', query);
  if (opts.lookup) params.set('lookup', '1');
  const body = await apiRequest(`${CREATOR_PAGES}/channel-groups?${params.toString()}`);
  return { groups: body.groups ?? [], warnings: body.warnings ?? [], cmStatus: body.cmStatus };
}

/**
 * Display metadata for an already-known channel set (a saved page's roster) in
 * one request — never loop searchChannelGroups over ids, which stampedes the
 * server's group cache.
 */
export async function getChannelGroupsByIds(channelIds: string[]): Promise<ChannelGroup[]> {
  if (channelIds.length === 0) return [];
  const body = await apiRequest(
    `${CREATOR_PAGES}/channel-groups?channelIds=${encodeURIComponent(channelIds.join(','))}`,
  );
  return body.groups ?? [];
}

export async function previewCreatorPage(input: {
  channelIds: string[];
  pageType?: CreatorPageType;
  includeAdvertisers?: boolean;
  includeAdSense?: boolean;
  clientLabel?: string;
  avatarUrl?: string;
  bannerUrl?: string;
}): Promise<any> {
  const body = await apiRequest(`${CREATOR_PAGES}/preview`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return body.report;
}
